import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { useAppDispatch } from "../../../../common/hooks/hooks";
import { closeGlobalModal } from "../../../../common/features/globalModal/globalModalSlice";
import Icon from "../../../../common/components/Icon/Icon";
import { useStockNotification } from "../hooks/useStockNotification";

interface StockNotificationFormProps {
  productId: number;
  variationId?: number;
  productName: string;
}

const StockNotificationForm: React.FC<StockNotificationFormProps> = ({
  productId,
  variationId,
  productName,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  // Initialize load timestamp for spam protection
  const [loadTimestamp] = useState(Math.floor(Date.now() / 1000));

  // Use our custom hook for logic
  const { handleSubscribe, isLoading } = useStockNotification();

  // Define Validation Schema locally
  const schema = z.object({
    email: z
      .string()
      .email(
        t("validation.emailInvalid", "Please enter a valid email address.")
      ),
    // Honeypot field (should remain empty)
    nickname: z.string().optional(),
    // Timestamp
    form_load_timestamp: z.number().optional(),
  });

  type FormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      form_load_timestamp: loadTimestamp,
    },
  });

  const onSubmit = async (data: FormData) => {
    // If honeypot is filled, simulate success but do nothing (Frontend Guard)
    if (data.nickname) {
      dispatch(closeGlobalModal());
      return;
    }

    const result = await handleSubscribe({
      email: data.email,
      product_id: productId,
      variation_id: variationId,
      nickname: data.nickname,
      form_load_timestamp: data.form_load_timestamp,
    });

    if (result.success) {
      dispatch(closeGlobalModal());
    }
  };

  return (
    <div className="stock-notification-form">
      <div className="stock-notification-form__info">
        <Icon iconName="bell" className="icon--stroke-thick" />
        <p>
          {t(
            "stockNotification.info",
            "Enter your email address to receive a notification when {{productName}} is back in stock.",
            { productName }
          )}
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="form-card">
        {/* Security Fields */}
        <input
          type="text"
          {...register("nickname")}
          style={{ display: "none" }}
          tabIndex={-1}
          autoComplete="off"
        />
        <input
          type="hidden"
          {...register("form_load_timestamp", { valueAsNumber: true })}
        />

        <div className="form-field">
          <label htmlFor="email" className="sr-only">
            {t("checkoutForm.fields.email", "Email Address")}
          </label>
          <input
            id="email"
            type="email"
            {...register("email")}
            placeholder={t("checkoutForm.fields.email", "Email Address")}
            className={errors.email ? "has-error" : ""}
            disabled={isLoading}
          />
          {errors.email && (
            <p className="form-field__error">{errors.email.message}</p>
          )}
        </div>

        <button
          type="submit"
          className="button button--primary"
          style={{ width: "100%", justifyContent: "center" }}
          disabled={isLoading}
        >
          {isLoading ? (
            <span className="loader-spinner"></span>
          ) : (
            t("stockNotification.submitButton", "Notify Me")
          )}
        </button>
      </form>
    </div>
  );
};

export default StockNotificationForm;
